/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 4 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl, getImageUrl } from "@Utils";
import type { ComponentProps } from "react";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import { useTranslation } from "@i18n";
import React, { useState, useRef, useEffect, useCallback } from "react";
import Accordion from "@CubeComponents/html/accordion/materialUiAccordian";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { RootState } from "@Reducers/index";
import SharingContent from "./SharingContent/SharingContent";
import VersionContent from "./VersionContent/VersionContent";
import RightSidebarFileExp from "@Components/RightSidebarFileExp/RightSidebarFileExp";
import DetailsContent from "./DetailsContent/DetailsContent";
import DependencyContent from "./DependencyContent/DependencyContent";
import ConstituentContent from "./ConstituentContent/ConstituentContent";
import ExternalLinksContent from "./ExternalLinksContent/ExternalLinksContent";
import { isExternalSharingEnabled } from "../ExternalLinks";
import AccessContent from "./AccessContent/AccessContent";
import { showToastNotification } from "@Reducers/toastSlice";
import { BIM_ENGINE, CAD_ENGINE, SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR } from "@Services/constants";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import RevisionContent from "./RevisionContent/RevisionContent";
import FolderRevisionSeriesContent from "./FolderRevisionSeriesContent/FolderRevisionSeriesContent";
import FileRevisionSeriesContent from "./FileRevisionSeriesContent/FileRevisionSeriesContent";
import IconComponent from "@CubeComponents/icons";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AccordionSummary, AccordionDetails } from "@mui/material";
import AllFileVersionStatusInReviewsModal from "@Features/reviews/AllFileVersionStatusInReviewsModal";
import FileStatusInReviews from "@Features/reviews/FileStatusInReviews";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { AiOutlinePlusCircle } from "react-icons/ai";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import { RxCross2 } from "react-icons/rx";
import NumericTextBoxComponent from "@CubeComponents/Mui_Form/NumericTextBoxComponent";
import MultiSelectComponent from "@CubeComponents/form/MultiselectComponent";
import axiosWrapper from "@AxiosWrapper";
import { useParams } from "react-router-dom";
import {
  maximiseFileUpload,
  setIsFileVersionUploading,
  setFileUploadProperties,
  setCurrentFileUploadProperties,
  selectedFilesFileManager,
} from "@Actions/fileUpload";
import CustomFieldsContent from "./CustomFieldsContent";
import type { SelectedFileItem } from "@ServerTypes/folders/folders.schema";
import _ from "lodash";
import type { GetFileDetailsResponse } from "@ServerTypes/fileOperations/fileOperations.schemas";
import type {
  GetFileNomenclatureResponse,
  SetFileNomenclatureResponse,
} from "@ServerTypes/fileUpload/fileUpload.schema";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import { ProjectFormTemplate } from "@Features/projects";
import Text from "@CubeComponents/html/Text/Text";
import { getIconbasedOnResourceType } from "@CubeComponents/constants/globalFunctions";
import { convertDate } from "@Services/luxonHelper";
import { buildDetailsContentProps, buildSharingContentProps } from "./fileVersionContentProps";
import { fetchDirectoryDataOfFile } from "@Actions/fileDirectoryData";
/** The folder nomenclature rule this panel edits. */
type NomenclatureField = {
  _id?: string;
  name?: string;
  type?: string;
  value?: string;
  options?: string[];
  allowedCharacters?: string;
};

/**
 * The file row this panel renders. `SelectedFileItem` plus the members the file-manager attaches and
 * this panel reads — creator/date display fields, the transmittal back-reference, and `path`.
 */
type FileVersionRow = SelectedFileItem & {
  dateCreated?: number;
  userId?: ObjectIdString;
  userName?: string;
  /** Breadcrumb path STRING, per `directoryDataFileItemSchema`. */
  path?: string;
  transmittalHasFileId?: ObjectIdString;
  project?: { _id?: ObjectIdString; projectId?: string };
};
type NomenclatureData = { separator?: string; fields?: NomenclatureField[] };

const DEFAULT_EMPTY_ARRAY = [] as FileVersionRow[];

function FileVersion({
  disableEngineType,
  onChange,
  fileVersionId = [],
  selectedFileDataFromFileManager = DEFAULT_EMPTY_ARRAY,
  isContainer = false,
  transmittalId,
}: {
  disableEngineType?: boolean;
  onChange?: (data: Record<string, unknown>) => void;
  fileVersionId?: string[];
  selectedFileDataFromFileManager?: FileVersionRow[];
  isContainer?: boolean;
  transmittalId?: ObjectIdString;
}) {
  const { t } = useTranslation();
  const params = useParams();
  const layout = useSelector((state: RootState) => state.layout);
  const fileUploadState = useSelector((state: RootState) => state.fileUploadState);
  const projectData = useSelector((state: RootState) => state.projectData);
  const fileUploadList = useSelector((state: RootState) => state.fileUploadList);
  const currentFolder = useSelector((state: RootState) => state.currentFolder);
  const dispatch = useDispatch();
  const specialCharRegex = /[\\/:*?"<>|]/;
  const versionManagementRef = useRef<HTMLDivElement | null>(null);
  const accordianInitialState = {
    openDetails: true,
    openSign: true,
    openNomenclature: true,
    openAccess: true,
    openConstituent: true,
    openSharing: true,
    openVersion: true,
    openDependency: true,
    openRevision: true,
    openStatusInReviews: true,
    openCustomFiles: true,
    openCustomMetadata: true,
    // Collapsed by default, unlike its neighbours: the section fetches its list when open, and most
    // items carry no external links, so defaulting to open would add a request per selection just to
    // render an empty state. The grid's link badge already signals when there IS something here.
    openExternalLinks: false,
  };

  const [openAllReviews, setOpenAllReviews] = useState(false);
  const [nomenclatureData, setNomenclatureData] = useState({
    separator: "",
    fields: [],
  });
  const [accordianState, setAccordianState] = useState<Record<string, boolean>>(accordianInitialState);
  const [selectedFormTemplateData, setSelectedFormTemplateData] = useState<{
    formTemplate?: string;
    formTemplateId?: string;
  }>({});
  const [updatedFormTemplateId, setUpdatedFormTemplateId] = useState<string | null>(null);
  const [separatorError, setSeparatorError] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<FileVersionRow[]>([]);
  const [nomenclatureFilled, setNomenclatureFilled] = useState<boolean>(false);
  const [multipleFilesSelected, setMultipleFilesSelected] = useState<boolean>(false);

  const getFileDirectoryData = async () => {
    const data = await fetchDirectoryDataOfFile(
      [{ field: isContainer ? "_id" : "fileVersionId", value: fileVersionId[0] }],
      transmittalId,
    );
    if (Array.isArray(data)) {
      setSelectedFile(data as FileVersionRow[]);
      dispatch(selectedFilesFileManager(data as SelectedFileItem[]));
    } else {
      dispatch(
        showToastNotification({
          content: SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  useEffect(
    () => () => {
      if (nomenclatureFilled && nomenclatureData && !nomenclatureData.separator) {
        handleNomenclatureUpdate({
          separator: "",
          fields: [],
        });
      }
    },

    [],
  );

  const selectedFileFromManagerId = selectedFileDataFromFileManager[0]?._id;
  const selectedFileFromManagerTransmittalId = selectedFileDataFromFileManager[0]?.id;
  useEffect(() => {
    setMultipleFilesSelected(false);
    setSelectedFile([]);
    if (selectedFileDataFromFileManager.length == 1) {
      setSelectedFile(selectedFileDataFromFileManager);
    } else if (selectedFileDataFromFileManager.length > 1) {
      setMultipleFilesSelected(true);
    } else if (fileVersionId?.length === 1) {
      getFileDirectoryData();
    } else if (fileVersionId?.length > 1) {
      setMultipleFilesSelected(true);
    }
  }, [
    fileVersionId?.length,
    selectedFileDataFromFileManager,
    selectedFileDataFromFileManager.length,
    selectedFileFromManagerId,
    selectedFileFromManagerTransmittalId, // patch for transmittal
  ]);

  let originalResourceType: string | undefined;

  const getFileNomenclature = async () => {
    const response = await axiosWrapper.post<GetFileNomenclatureResponse>(
      `${getBackendUrl()}/fileUpload/getFileNomenclature`,
      {
        folderId: selectedFile[0]!.hierarchyId,
      },
    );
    if (response.data.data) {
      setNomenclatureData(response.data.data as Parameters<typeof setNomenclatureData>[0]);
    } else {
      setNomenclatureData({
        separator: "",
        fields: [],
      });
    }
  };

  useEffect(() => {
    if (_.get(selectedFile, "[0].hierarchyId")) {
      let { resourceType } = selectedFile[0]!;

      if (resourceType == "RESOURCE_TYPE_SHORTCUT_FILE") {
        originalResourceType = "RESOURCE_TYPE_SHORTCUT_FILE";
        resourceType = "RESOURCE_TYPE_FILE";
      } else if (resourceType == "RESOURCE_TYPE_SHORTCUT_MERGED_FILE") {
        originalResourceType = "RESOURCE_TYPE_SHORTCUT_MERGED_FILE";
        resourceType = "RESOURCE_TYPE_MERGED_FILE";
      }
      if (resourceType == "RESOURCE_TYPE_FOLDER") {
        getFileNomenclature();
      }
    }
  }, [selectedFile]);

  const getFormTemplate = useCallback(async () => {
    if (selectedFile?.[0]?.resourceType === "RESOURCE_TYPE_TRANSMITTAL") return;
    const payload = {
      id: selectedFile[0]!.hierarchyId,
    };
    const response = await axiosWrapper.post<GetFileDetailsResponse>(
      `${getBackendUrl()}/fileOperations/getFileDetails`,
      payload,
    );
    if (response.data) {
      setSelectedFormTemplateData({
        formTemplate: response.data.formTemplate,
        formTemplateId: response.data.formTemplateId,
      });
    }
  }, [selectedFile]);

  useEffect(() => {
    if (_.get(selectedFile, "[0].hierarchyId")) {
      getFormTemplate();
    }
  }, [selectedFile, updatedFormTemplateId, getFormTemplate]);

  const accessContent = () => (
    <AccessContent
      resourceId={selectedFile[0]!.hierarchyId}
      resourceType={selectedFile[0]!.resourceType!}
      parentId={selectedFile[0]!.parentId}
      transmittalId={transmittalId ?? selectedFile[0]!.transmittalVersionId}
      transmittalHasFileId={selectedFile[0]!.transmittalHasFileId}
      spaceId={selectedFile[0]!.spaceId!}
      pathofFolder={selectedFile[0]!.path}
    />
  );

  const handleNomenclatureUpdate = (data: NomenclatureData) => {
    setNomenclatureData(data as Parameters<typeof setNomenclatureData>[0]);
    if (
      (data.separator && data.fields?.length && !specialCharRegex.test(data.separator)) ||
      (!data.separator && !data.fields!.length)
    ) {
      setSeparatorError("");
      saveFileNomenclature(data);
    }
  };

  const handleSeparatorChange = (args: React.ChangeEvent<HTMLInputElement>) => {
    setNomenclatureFilled(true);
    const separatorValue = args.target.value;

    if (!separatorValue && nomenclatureData.fields?.length) {
      setSeparatorError("Separator cannot be empty");
    } else if (specialCharRegex.test(separatorValue)) {
      setSeparatorError('Separator cannot contain any of the following characters: \\ / : * ? " < > |');
    } else {
      setSeparatorError("");
    }
    handleNomenclatureUpdate({
      ...nomenclatureData,
      separator: separatorValue,
    });
  };
  const handleRemoveField = (e: React.MouseEvent) => {
    // `NamedNodeMap` declares only a numeric index signature; named access is legacy platform
    // behaviour that works at runtime. Erased assertion.
    const index = ((e.currentTarget as HTMLElement).attributes as NamedNodeMap & Record<string, Attr>)["data-index"]!
      .value;
    const tempNomenclatureData = { ...nomenclatureData };
    tempNomenclatureData.fields.splice(Number(index), 1);
    handleNomenclatureUpdate(tempNomenclatureData);
  };

  const handleCreateNewField = () => {
    handleNomenclatureUpdate({
      ...nomenclatureData,
      fields: [
        ...nomenclatureData.fields,
        {
          type: "text",
        },
      ],
    });
  };

  const nomenclatureFields = [
    {
      text: "Text",
      value: "text",
    },
    {
      text: "Single Select",
      value: "single select",
    },
    {
      text: "Number",
      value: "number",
    },
  ];

  const handleFieldTypeChange = (value: string | undefined, index: number) => {
    const tempNomenclatureData: NomenclatureData = { ...nomenclatureData };
    tempNomenclatureData.fields![index]!.type = value;
    handleNomenclatureUpdate(tempNomenclatureData);
  };

  const saveFileNomenclature = async (nomenclatureData: NomenclatureData) => {
    if (!selectedFile.length) return;
    if (!selectedFile[0]?.permission?.write) {
      showToast();
      return;
    }
    const data = {
      folderId: selectedFile[0]!.hierarchyId,
      fileNomenclature: nomenclatureData,
    };
    await axiosWrapper.post<SetFileNomenclatureResponse>(`${getBackendUrl()}/fileUpload/setFileNomenclature`, data);
  };

  const openAllReviewsCallback = () => {
    setOpenAllReviews(true);
  };

  const handleFieldValueChange = (args: { value?: string; target?: { value?: string } }, index: number) => {
    const tempNomenclatureData: NomenclatureData = { ...nomenclatureData };
    tempNomenclatureData.fields![index]!.allowedCharacters = args.value;
    handleNomenclatureUpdate(tempNomenclatureData);
  };

  const handleSingleSelectOptionsChange = (args: { value?: string[] }, index: number) => {
    const tempNomenclatureData: NomenclatureData = { ...nomenclatureData };
    tempNomenclatureData.fields![index]!.options = args.value;
    handleNomenclatureUpdate(tempNomenclatureData);
  };
  const nomenclatureContent = () => (
    <div className="text-sm flex flex-col gap-4">
      <div className="flex gap-4 items-center">
        <span>
          <Text>{t("Separator")}</Text>
        </span>
        <div className="flex flex-col gap-1">
          <TextFieldComponent
            placeholder={t("Enter Separator")}
            value={nomenclatureData.separator}
            onChange={handleSeparatorChange}
            width="7.5rem"
          />
          {separatorError ? (
            <span className="text-red-600 text-sm">
              <Text>{separatorError}</Text>
            </span>
          ) : null}
        </div>
      </div>

      {nomenclatureData.fields?.map((data: NomenclatureField, index: number) => (
        <div key={`nomenclatureRules_${index}`} className="flex flex-wrap gap-4 items-center">
          <span className="shrink-0">
            <Text>{`Field ${index + 1}`}</Text>
          </span>
          <div className="flex flex-wrap gap-4 items-center">
            <AutoComplete
              options={nomenclatureFields}
              valueField={"value" as "text"}
              getOptionLabel={(option: { text?: string }) => option?.text || ""}
              value={data.type}
              onChange={(_event: unknown, newValue: { text?: string; value?: string } | null) => {
                handleFieldTypeChange(newValue?.value, index);
              }}
              sx={{ width: "7.5rem", minWidth: "7.5rem" }}
              renderInput={(params) => <TextFieldComponent {...params} placeholder={t("Select Type")} />}
            />
            {(["text", "number"] as (string | undefined)[]).includes(data.type) && (
              <NumericTextBoxComponent
                placeholder={t("Allowed no. of characters")}
                value={data.allowedCharacters as unknown as number}
                change={(e) => handleFieldValueChange(e as { value?: unknown } as { value?: string }, index)}
                width="12rem"
                min={0}
              />
            )}
            {data.type == "single select" && (
              <MultiSelectComponent
                value={data.options}
                allowCustomValue
                // The shared MultiSelect payload types its members as `unknown`; this picker's
                // datasource is string-valued. Erased assertion at the forwarding boundary.
                change={(args) => handleSingleSelectOptionsChange(args as { value?: string[] }, index)}
                data-index={index}
                width="8.75rem"
              />
            )}
            <RxCross2 data-index={index} onClick={handleRemoveField} className="cursor-pointer shrink-0" />
          </div>
        </div>
      ))}
      <AiOutlinePlusCircle onClick={handleCreateNewField} size={18} className="cursor-pointer" />
    </div>
  );

  const customFieldsContent = () => (
    <CustomFieldsContent
      hierarchyId={selectedFile[0]!.hierarchyId as string}
      setUpdatedFormTemplateId={setUpdatedFormTemplateId}
      selectedFormTemplateData={selectedFormTemplateData}
      resourceType={selectedFile[0]!.resourceType!}
      accessPermission={selectedFile[0]!.permission!}
      onChange={onChange}
    />
  );

  const customMetadataContent = () => (
    <ProjectFormTemplate
      // This panel only renders with a selection and inside a project, so both are present; the row
      // type declares more than the template panel reads. Erased assertions at the one boundary.
      selectedFile={selectedFile[0] as ComponentProps<typeof ProjectFormTemplate>["selectedFile"]}
      projectId={projectData._id!}
      resourceType={selectedFile[0]!.engineType == "BIM" ? "model" : "drawing"}
      engineType={selectedFile[0]!.engineType}
    />
  );

  const sharingContent = () =>
    selectedFile[0]!.permission && !selectedFile[0]!.permission.share ? (
      <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={showToast} data-testid="shareContentBtn">
        <Text>{t("Transmit")}</Text>
      </ButtonComponent>
    ) : (
      <SharingContent
        {...buildSharingContentProps(selectedFile[0]!, {
          transmittalId,
        })}
      />
    );

  const revisionContent = () => {
    const { resourceType, hierarchyId } = selectedFile[0]!;
    if (resourceType === "RESOURCE_TYPE_FOLDER") {
      return <FolderRevisionSeriesContent folderId={hierarchyId as string} />;
    }
    return (
      <>
        <FileRevisionSeriesContent fileId={hierarchyId as string} />
        <RevisionContent
          {...{
            resourceType: resourceType,
            resourceId: hierarchyId,
          }}
        />
      </>
    );
  };

  const fileStatusInReviewsContent = () => (
    <FileStatusInReviews
      fileVersionId={selectedFile[0]!.fileVersion as ObjectIdString & string}
      openAllReviewsCallback={openAllReviewsCallback}
      spaceId={selectedFile[0]!.spaceId!}
    />
  );

  const showToast = () => {
    dispatch(
      showToastNotification({
        content: t("Insufficient permission to perform this action."),
        type: TOAST_TYPE_ERROR,
      }),
    );
  };

  const dependencyContent = () => (
    <DependencyContent
      workspaceId={params.workspaceId}
      fileVersionId={selectedFile[0]!.fileVersion as ObjectIdString & string}
      isEmptyContainer={selectedFile[0]!.isEmptyContainer}
      accessPermission={selectedFile[0]!.permission!}
      projectId={(selectedFile[0]!.project?._id || projectData.id) as string}
    />
  );

  const versionContent = () => (
    <VersionContent
      filesData={selectedFile[0]}
      enableFileManagerContent={true}
      enableCostdatabaseContent={false}
      enableClashContent={false}
      clashVersionsDetails={[]}
      costDatabaseDetails={null}
      handleFileUploadButtonClick={handleFileUploadButtonClick}
      originalResourceType={originalResourceType!}
      spaceId={(selectedFile[0]!.spaceId || params.workspaceId) as string}
    />
  );

  const getUserAvatar = (id: string) => <UserNameTagComponent id={id} />;

  const detailsContent = () => (
    <DetailsContent
      {...buildDetailsContentProps(selectedFile[0]!, {
        disableEngineType,
        transmittalId,
        getUserAvatar,
        projectId: projectData.id,
      })}
    />
  );

  const externalLinksContent = () => (
    <ExternalLinksContent
      resourceId={selectedFile[0]!.hierarchyId}
      spaceId={selectedFile[0]!.spaceId}
      name={selectedFile[0]!.name}
      resourceType={selectedFile[0]!.resourceType}
      // Same proxy the context menu uses. The row carries no flag for full access
      // (RESOURCE_CHANGE_PERMISSION is never mapped by `resolvePermissions`), and no full-access user
      // lacks `share`, so this withholds Manage from users who certainly cannot use it while the
      // server stays the authority.
      canManage={!!selectedFile[0]!.permission?.share}
    />
  );
  const constituentContent = () => (
    <ConstituentContent
      {...({
        fileVersionId: selectedFile[0]!.fileVersion,
        hierarchyId: selectedFile[0]!.hierarchyId,
        workspaceId: params.workspaceId,
        accessPermission: selectedFile[0]!.permission,
        MergeName: selectedFile[0]!.name,
        resourceType: selectedFile[0]!.resourceType,
        projectId: selectedFile[0]!.project?._id || projectData.id,
      } as Record<string, unknown>)}
    />
  );
  const handleFileUploadButtonClick = () => {
    if (layout.FILE_MANAGER?.IMPORT_ONLY_UPLOADS) {
      return;
    }
    if (!selectedFile[0]!.permission?.upload) {
      showToast();
      return;
    }
    if (fileUploadState != "CLOSED") {
      dispatch(
        showToastNotification({
          content: t("Please close other upload operations before proceeding!"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else {
      dispatch(
        setFileUploadProperties({
          [`${selectedFile[0]!.spaceId}_${selectedFile[0]!.hierarchyId}`]: {
            workspaceId: selectedFile[0]!.spaceId,
            fileUploadList,
          },
        }),
      );
      dispatch(
        setCurrentFileUploadProperties({
          workspaceId: selectedFile[0]!.spaceId,
          hierarchyId: selectedFile[0]!.hierarchyId,
          currentFolderRef: currentFolder ?? undefined,
          isFolderUpload: false,
          isUploadAllowed: true,
        }),
      );
      dispatch(setIsFileVersionUploading(true));
      dispatch(maximiseFileUpload());
    }
  };

  const handleSingleAccordionStateUpdate = (type: string) => {
    setAccordianState({
      ...accordianState,
      [type]: !accordianState[type],
    });
  };

  const accordionContentMapper: Record<string, () => React.ReactNode> = {
    details: detailsContent,
    customFields: customFieldsContent,
    CustomMetadata: customMetadataContent,
    access: accessContent,
    nomenclature: nomenclatureContent,
    constituentFiles: constituentContent,
    externalLinks: externalLinksContent,
    transmittals: sharingContent,
    verisons: versionContent,
    reviews: fileStatusInReviewsContent,
    dependencies: dependencyContent,
    revisions: revisionContent,
  };

  const accordionHeader = (data: {
    id: string;
    name?: string;
    headerName?: string;
    headerIcon?: string;
    dataTestIdHeaderIcon?: string;
    dataTestIdHeaderName?: string;
  }) => (
    <div className="flex ps-5 items-center gap-4">
      <IconComponent dataTestId={data.dataTestIdHeaderIcon} name={data.headerIcon} width="1.25rem" height="1.25rem" />
      <div className="font-semibold">
        <Text data-testid={data.dataTestIdHeaderName}>{data.headerName}</Text>
      </div>
    </div>
  );

  const accordionContent = (data: { id: string }) => <div className="ps-3">{accordionContentMapper[data.id]!()}</div>;

  const versionAccordion = () => {
    const resourceType = selectedFile[0]?.resourceType;

    const sidebarMapper = [
      {
        id: "details",
        headerName: t("Details"),
        headerIcon: "details",
        allow: true,
        accordionState: "openDetails",
        dataTestIdHeader: "detailsHeader",
        dataTestIdHeaderName: "detailsHeaderName",
        dataTestIdContent: "detailsHeaderContent",
        dataTestIdHeaderIcon: "detailsHeaderIcon",
      },
      {
        id: "customFields",
        headerName: (["RESOURCE_TYPE_FOLDER"] as (string | undefined)[]).includes(resourceType)
          ? t("Templates")
          : t("Custom Fields"),
        headerIcon: "CustomFields",
        allow:
          selectedFormTemplateData.formTemplate?.length ||
          (["RESOURCE_TYPE_FOLDER"] as (string | undefined)[]).includes(resourceType),
        accordionState: "openCustomFiles",
      },
      {
        id: "CustomMetadata",
        headerName: t("Templates"),
        headerIcon: "CustomMetadata",
        allow: [BIM_ENGINE, CAD_ENGINE].includes(_.get(selectedFile, "[0].engineType")!),
        accordionState: "openCustomMetadata",
      },
      {
        id: "access",
        headerName: t("Access"),
        dataTestIdHeader: "accesssHeader",
        dataTestIdContent: "accesssHeaderContent",
        headerIcon: "access",
        allow: (
          [
            "RESOURCE_TYPE_FOLDER",
            "RESOURCE_TYPE_MERGED_FILE",
            "RESOURCE_TYPE_FILE",
            "RESOURCE_TYPE_TRANSMITTAL",
            "RESOURCE_TYPE_SHORTCUT_FILE",
            "RESOURCE_TYPE_EMBED",
          ] as (string | undefined)[]
        ).includes(resourceType),
        accordionState: "openAccess",
      },
      {
        id: "externalLinks",
        headerName: t("External Links"),
        headerIcon: "Link",
        // Files and folders only, matching the server's `resourceType` enum — sharing a transmittal,
        // estimate or schedule is an explicit v1 non-goal, so the section is absent rather than
        // present-and-empty on those rows. Gated on the portal switch too (PRD 2).
        allow:
          isExternalSharingEnabled(layout) &&
          (
            ["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_MERGED_FILE", "RESOURCE_TYPE_FOLDER"] as (string | undefined)[]
          ).includes(resourceType),
        accordionState: "openExternalLinks",
        dataTestIdHeader: "externalLinksHeader",
        dataTestIdHeaderName: "externalLinksHeaderName",
        dataTestIdHeaderIcon: "externalLinksHeaderIcon",
        dataTestIdContent: "externalLinksHeaderContent",
      },
      {
        id: "nomenclature",
        headerName: t("Nomenclature"),
        headerIcon: "nomenclatureIcon",
        allow: (["RESOURCE_TYPE_FOLDER"] as (string | undefined)[]).includes(resourceType),
        accordionState: "openNomenclature",
      },
      {
        id: "constituentFiles",
        headerName: t("Constituent Files"),
        headerIcon: "ConstituentFilesIcon",
        allow: (["RESOURCE_TYPE_MERGED_FILE"] as (string | undefined)[]).includes(resourceType),
        accordionState: "openConstituent",
        dataTestIdHeader: "constituentFilesHeader",
        dataTestIdHeaderName: "constituentFilesHeaderName",
        dataTestIdHeaderIcon: "constituentFilesHeaderIcon",
        dataTestIdContent: "constituentFilesContent",
      },
      {
        id: "transmittals",
        headerName: t("Transmittals"),
        headerIcon: "Transmittals",
        allow:
          layout.TRANSMITTAL?.ENABLE &&
          (
            ["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_MERGED_FILE", "RESOURCE_TYPE_SHORTCUT_FILE"] as (string | undefined)[]
          ).includes(resourceType),
        accordionState: "openSharing",
      },
      {
        id: "verisons",
        headerName: t("Versions"),
        headerIcon: "VersionsIcon",
        allow: (
          ["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_MERGED_FILE", "RESOURCE_TYPE_SHORTCUT_FILE", "RESOURCE_TYPE_EMBED"] as (
            | string
            | undefined
          )[]
        ).includes(resourceType),
        accordionState: "openVersion",
      },
      {
        id: "reviews",
        headerName: t("Reviews"),
        headerIcon: "Review",
        allow: (["RESOURCE_TYPE_FILE"] as (string | undefined)[]).includes(resourceType),
        accordionState: "openStatusInReviews",
      },
      {
        id: "dependencies",
        headerName: t("Dependencies"),
        headerIcon: "DependenciesIcon",
        allow:
          layout.FILE_PROPERTIES?.DEPENDENCIES?.ENABLE &&
          (["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_SHORTCUT_FILE"] as (string | undefined)[]).includes(resourceType),
        accordionState: "openDependency",
      },
      {
        id: "revisions",
        headerName: t("Revisions"),
        headerIcon: "RevisionsIcon",
        allow:
          (
            [
              "RESOURCE_TYPE_FILE",
              "RESOURCE_TYPE_MERGED_FILE",
              "RESOURCE_TYPE_SHORTCUT_FILE",
              "RESOURCE_TYPE_FOLDER",
            ] as (string | undefined)[]
          ).includes(resourceType) && layout.FILE_PROPERTIES?.REVISION?.ENABLE,
        accordionState: "openRevision",
      },
    ];

    return (
      <>
        {sidebarMapper.map(
          (data) =>
            data.allow && (
              <Accordion
                key={data.id}
                expanded={accordianState[data.accordionState]}
                onChange={() => handleSingleAccordionStateUpdate(data.accordionState)}
              >
                <AccordionSummary data-testid={data.dataTestIdHeader} expandIcon={<ExpandMoreIcon />}>
                  {accordionHeader(data)}
                </AccordionSummary>
                <AccordionDetails data-testid={data.dataTestIdContent}>{accordionContent(data)}</AccordionDetails>
              </Accordion>
            ),
        )}
      </>
    );
  };

  const handleAccordionStateUpdate = (type: string) => {
    setAccordianState({
      [type]: true,
    });
  };

  const closeFileVersionReviewStatusModal = () => {
    setOpenAllReviews(false);
  };

  const renderSidebarHeader = () => (
    <div className="flex flex-col gap-4 justify-between p-4 rounded-t-[0.5rem] border-b border-b-[#C6D8EC] bg-[#FAFCFF]">
      <div className="flex items-center gap-2 min-w-0">
        {/* @ts-expect-error -- an indexed `FileVersionRow` is passed unguarded where the callee requires a value; the `| undefined` is `noUncheckedIndexedAccess`, and the surrounding code has already bounds-checked the index. */}
        {getIconbasedOnResourceType(selectedFile[0], "h-6 w-6")}
        <Text className="text-[1.25rem] font-semibold text-[#192048] truncate max-w-[20rem]">
          {selectedFile[0]?.name}
        </Text>
      </div>
      {(selectedFile[0]?.userName || selectedFile[0]?.dateCreated) && (
        <div className="flex items-center justify-between">
          <UserNameTagComponent fullName={selectedFile[0]?.userName} id={selectedFile[0]?.userId} size="LG" isCircle />
          {selectedFile[0]?.dateCreated ? (
            <Text className="text-sm text-gray-500">
              {convertDate(Number(selectedFile[0]!.dateCreated), "dateForText")}
            </Text>
          ) : null}
        </div>
      )}
    </div>
  );

  return (
    <>
      {Array.isArray(selectedFile) && selectedFile.length && !multipleFilesSelected ? (
        <div className="flex flex-col w-full h-full rounded-t-[0.5rem] border border-[#C6D8EC] overflow-hidden">
          {renderSidebarHeader()}
          <div className="flex w-full flex-1 min-h-0 overflow-auto">
            <div ref={versionManagementRef} className="h-full w-full overflow-auto rounded bg-[var(--surface-white)]">
              {versionAccordion()}
            </div>
            <RightSidebarFileExp handleAccordionStateUpdate={handleAccordionStateUpdate} />
          </div>
        </div>
      ) : (
        !multipleFilesSelected && (
          <div className="flex w-full h-full bg-white items-center justify-center flex-col">
            <img src={`${getImageUrl()}/NoData.svg`} alt="No data" />
            <Text>{t("No file selected")}</Text>
          </div>
        )
      )}
      {multipleFilesSelected && (
        <div className="flex w-full h-full bg-white items-center justify-center flex-col">
          <img src={`${getImageUrl()}/NoData.svg`} alt="No data" />
          <Text>{t("Multiple Files Selected")}</Text>
        </div>
      )}

      {openAllReviews && (
        <AllFileVersionStatusInReviewsModal
          fileId={selectedFile[0]!.hierarchyId as ObjectIdString}
          closeModal={closeFileVersionReviewStatusModal}
        />
      )}
    </>
  );
}

export default FileVersion;
